import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FLIGHT_ENDPOINTS, USER_ENDPOINTS } from '../config/api';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  CardActions,
  Grid,
  Divider,
  CardActionArea,
  Tooltip,
  Switch
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ViewIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import FlightLandIcon from '@mui/icons-material/FlightLand';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

const Dashboard = () => {
  const [flights, setFlights] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  dayjs.extend(utc)

  useEffect(() => {
    fetchFlights();
    fetchWebhooks();
  }, []);

  const fetchWebhooks = async () => {
    try {
      const response = await fetch(USER_ENDPOINTS.WEBHOOKS, {
        headers: getAuthHeader(),
      });

      if (response.ok) {
        const data = await response.json();
        setWebhooks(data.webhooks);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch webhooks');
      }
    } catch (err) {
      setError('Something went wrong while fetching webhooks');
    }
  };

  const fetchFlights = async () => {
    try {
      setLoading(true);
      const response = await fetch(FLIGHT_ENDPOINTS.LIST, {
        headers: getAuthHeader(),
      });

      if (response.ok) {
        const data = await response.json();
        setFlights(data);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch flights');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (id, newState) => {
    try {
      const response = await fetch(FLIGHT_ENDPOINTS.TOGGLE(id), {
        method: 'PATCH',
        headers: getAuthHeader(),
      });
  
      if (!response.ok) {
        throw new Error('Failed to toggle tracker state');
      }
  
      // Optional: If your backend returns the updated tracker
      const updated = await response.json();
  
      // Update state locally
      setFlights(prev =>
        prev.map(flight =>
          flight.id === id ? { ...flight, active: updated.active } : flight
        )
      );
    } catch (err) {
      console.error('Toggle failed', err);
      // Optional: show snackbar or error feedback
    }
  };
  

  const handleDelete = async (flightId) => {
    if (window.confirm('Are you sure you want to delete this flight?')) {
      try {
        const response = await fetch(FLIGHT_ENDPOINTS.DETAIL(flightId), {
          method: 'DELETE',
          headers: getAuthHeader(),
        });

        if (response.ok) {
          setFlights(flights.filter(flight => flight.id !== flightId));
        } else {
          const errorData = await response.json();
          setError(errorData.error || 'Failed to delete flight');
        }
      } catch (err) {
        setError('Something went wrong. Please try again.');
      }
    }
  };

  const handleEdit = (flightId) => {
    navigate(`/flights/edit/${flightId}`);
  };

  const handleAddNew = () => {
    navigate('/flights/add');
  };

  const handleView = (flightId) => {
    navigate(`/flights/${flightId}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return dayjs.utc(dateString).format('DD/MM/YYYY, HH:mm');
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getWebhookName = (webhookId) => {
    if (!webhookId || !Array.isArray(webhooks)) return 'No webhook';
  
    const webhook = webhooks.find((webhook) => webhook.id === webhookId);
    return webhook ? webhook.name : 'No webhook';
  };

  // Mobile card view for flights
  const renderMobileFlightCards = () => {
    return (
      <Grid container spacing={2} sx={{ width: '100%' }}>
        {flights.map((flight) => (
          <Grid item sx={{ width: '100%' }} key={flight.id}>
            <Card sx={{ width: '100%' }}>
              <CardActionArea onClick={() => handleView(flight.id)}>
                <CardContent>
                  <Typography variant="h6" component="div" gutterBottom>
                    {flight.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {flight.airline}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <FlightTakeoffIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="body2">{flight.origin}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <FlightLandIcon sx={{ mr: 1, color: 'secondary.main' }} />
                    <Typography variant="body2">{flight.destination}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Departure: {formatDate(flight.outboundDeparture)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Return: {formatDate(flight.inboundDeparture)}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Webhook: {getWebhookName(flight.webhookId)}
                  </Typography>
                </CardContent>
              </CardActionArea>
              <Divider />
              <CardActions sx={{ justifyContent: 'flex-end' }}>
                <Tooltip title={flight.active ? "Deactivate tracker" : "Activate tracker"}>
                  <Switch
                    checked={flight.active}
                    onChange={() => handleToggleActive(flight.id, !flight.active)}
                    color="primary"
                  />
                </Tooltip>
                <IconButton
                  size="small"
                  onClick={() => handleView(flight.id)}
                  color="default"
                >
                  <ViewIcon />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleEdit(flight.id)}
                  color="primary"
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleDelete(flight.id)}
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  // Desktop table view for flights
  const renderDesktopFlightTable = () => {
    return (
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="flights table">
          <TableHead>
            <TableRow>
              <TableCell>Tracker Name</TableCell>
              <TableCell>Airline</TableCell>
              <TableCell>From</TableCell>
              <TableCell>To</TableCell>
              <TableCell>Departure</TableCell>
              <TableCell>Return</TableCell>
              <TableCell>Webhook</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {flights.map((flight) => (
              <TableRow
                key={flight.id}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                hover
              >
                <TableCell component="th" scope="row" onClick={() => handleView(flight.id)} sx={{cursor: 'pointer'}}>
                  {flight.name}
                </TableCell>
                <TableCell onClick={() => handleView(flight.id)} sx={{cursor: 'pointer'}}>
                  {flight.airline}
                </TableCell>
                <TableCell onClick={() => handleView(flight.id)} sx={{cursor: 'pointer'}}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <FlightTakeoffIcon sx={{ mr: 1, color: 'primary.main' }} />
                    {flight.origin}
                  </Box>
                </TableCell>
                <TableCell onClick={() => handleView(flight.id)} sx={{cursor: 'pointer'}}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <FlightLandIcon sx={{ mr: 1, color: 'secondary.main' }} />
                    {flight.destination}
                  </Box>
                </TableCell>
                <TableCell onClick={() => handleView(flight.id)} sx={{cursor: 'pointer'}}>
                  {formatDate(flight.outboundDeparture)}
                </TableCell>
                <TableCell onClick={() => handleView(flight.id)} sx={{cursor: 'pointer'}}>
                  {formatDate(flight.inboundDeparture)}
                </TableCell>
                <TableCell onClick={() => handleView(flight.id)} sx={{cursor: 'pointer'}}>
                    {getWebhookName(flight.webhookId)}
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title={flight.active ? "Deactivate tracker" : "Activate tracker"}>
                      <Switch
                        checked={flight.active}
                        onChange={() => handleToggleActive(flight.id, !flight.active)}
                        color="primary"
                      />
                    </Tooltip>
                    {/* <IconButton
                      size="small"
                      onClick={() => handleView(flight.id)}
                      color="default"
                    >
                      <ViewIcon />
                    </IconButton> */}
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(flight.id)}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(flight.id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 0, mb: 4, px: { xs: 2, sm: 3 } }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between', 
        alignItems: isMobile ? 'center' : 'center', 
        mb: 3, 
        mt: 1,
        width: '100%'
      }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ mb: isMobile ? 2 : 0, textAlign: isMobile ? 'center' : 'left' }}>
          My Trackers
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          gap: 2,
          width: isMobile ? '100%' : 'auto',
          alignItems: 'center'
        }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
            fullWidth={isMobile}
          >
            Add New Tracker
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, width: '100%' }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, width: '100%' }}>
          <CircularProgress />
        </Box>
      ) : flights.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center', width: '100%' }}>
          <Typography variant="h6" color="text.secondary">
            You don't have any tracked flights yet.
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
            sx={{ mt: 2 }}
          >
            Add Your First Flight Tracker
          </Button>
        </Paper>
      ) : (
        <Box sx={{ width: '100%', flex: 1 }}>
          {isMobile ? renderMobileFlightCards() : renderDesktopFlightTable()}
        </Box>
      )}
    </Container>
  );
};

export default Dashboard; 